// =============================================================================
// 조직·그룹·역할 명칭 사전 — 화면 전체가 같은 말을 쓰도록 한 곳에서 관리한다.
// 근거: docs/ORG_GROUP_CONCEPT_AUDIT.md §3-3.
//
//  · 조직(org)            고객 관점의 한 고객사. 운영사(H9) 화면에서는 '고객사'라 부른다.
//  · 그룹(device_groups)  조직 안의 권한 경계. 기기·계정·콘텐츠 소유·승인 방식이 걸린다.
//  · 역할(Owner/Admin/Editor/Viewer)  조직 역할. Admin은 central 여부로 중앙/그룹 관리자가 갈린다.
//  · '최고 관리자'는 H9 운영자(bo_operators)에게만 쓴다. 조직 Owner는 '조직 관리자'다.
// =============================================================================

export type OrgRole = 'Owner' | 'Admin' | 'Editor' | 'Viewer';
export type ApprovalMode = 'central' | 'group' | 'auto';

export const TERM = {
  org: '조직',
  tenant: '고객사',
  group: '그룹',
  /** 2026-09-14 '조직 관리'→'구성 관리': 그룹·디바이스·관리자를 한 메뉴로. 전 역할에 보이고 역할별로 탭이 줄어든다. */
  orgAdminMenu: '구성 관리',
  groupDevicesTab: '그룹 · 디바이스',
  tenantMenu: '고객사 관리',
  platformAdmin: '최고 관리자 (H9)',
  centralAdmin: '중앙 관리자',
} as const;

export const ROLE_LABELS: Record<OrgRole, string> = {
  Owner: '조직 관리자',
  Admin: '그룹 관리자',
  Editor: '편집자',
  Viewer: '조회자',
};

export const ROLE_DESCRIPTIONS: Record<OrgRole, string> = {
  Owner: '조직 전체 정책 · 승인 · 계정 관리',
  Admin: '담당 그룹의 콘텐츠 · 기기 · 승인 (중앙 지정 시 전체 그룹)',
  Editor: '담당 그룹의 콘텐츠 등록 · 송출 제출(승인 대기)',
  Viewer: '담당 그룹 현황 조회',
};

export const ROLE_OPTIONS = (['Owner', 'Admin', 'Editor', 'Viewer'] as const).map((value) => ({
  value,
  label: ROLE_LABELS[value],
  desc: ROLE_DESCRIPTIONS[value],
}));

export const APPROVAL_MODE_LABELS: Record<ApprovalMode, string> = {
  central: '중앙 승인',
  group: '그룹 승인',
  auto: '자동 승인',
};

export function roleLabel(role: string | null | undefined): string {
  return role && role in ROLE_LABELS ? ROLE_LABELS[role as OrgRole] : role ?? '';
}

/** 작업 권한 표시(GNB). 그룹 스코프 적용 전에는 Admin을 구분하지 않는다. */
export function describeWorkRole(actor: { role: string | null; central: boolean; enforced: boolean }): string {
  if (actor.role === 'Owner') return actor.enforced ? `${ROLE_LABELS.Owner} · 전체 그룹` : ROLE_LABELS.Owner;
  if (actor.role === 'Admin') return !actor.enforced ? '관리자' : actor.central ? TERM.centralAdmin : ROLE_LABELS.Admin;
  if (actor.role === 'Editor') return ROLE_LABELS.Editor;
  if (actor.role === 'Viewer') return ROLE_LABELS.Viewer;
  return '담당 권한 없음';
}

/** 관리자 목록의 ‘담당 그룹’ 칸. */
export function describeGroupScope(member: { role: string; central?: boolean | undefined }, assignments: string[]): string {
  if (member.role === 'Owner') return '전체 그룹';
  if (member.role === 'Admin' && member.central) return `${TERM.centralAdmin} · 전체 그룹`;
  return assignments.join(', ') || '배정 없음';
}
